import React, { useCallback, useState, useEffect, memo } from 'react';
import Helmet from 'react-helmet';
import LineGraph from 'components/shared/graphs/LineGraph'
import { formatCurrency } from 'lib/utils/currency';
import Dropdown from 'components/shared/app/Dropdown';
import { renderData, renderOptions } from './utils/renderer'
import GeneralMessage from './components/GeneralMessage'
import { getMaxMin } from './utils/scaleOptions'
import { getStoreDate, ensure7Days, tzDate, last7, previous7, today, now, thisMonthLastYear, previousMonthLastYear, twoMonths, createIsoTimeRange } from 'utils/DateUtils';
import { calculateMetricsAcrossDays, NEW_DEFAULT_METRICS } from 'utils/adaya/ProviderUtils';
import { orZero } from 'lib/utils/NumUtils';
import { get } from 'lodash';
import { ErrorMessages } from 'lib/AppErrors';
import { ManagedRequest, RequestStatus } from 'lib/RequestManager';
import { getAccountProviders, getAccountTimezone, getGoal, getInnerError, renderError, renderLoader, renderNoDataMessage } from 'lib/utils/adaya/AdayaUtils';
import { PageHelper } from 'lib/helpers/PageHelper';
import { ProviderNames } from 'lib/data/models/Providers';
import { getLogger } from 'utils/logging';
import Config from 'lib/Config';
import { getMetricLongName, goalItems, gradientColors, metricItems, getMetricProp } from './utils/trendsUtils';
import { Goal, NEW_DEFAULT_GOALS } from 'lib/data/models/Goals';
import './style.scss';

const { log, warn } = getLogger('Trends', { color: 'yellow', enabled: false });

const pageHelper = new PageHelper();

export default Trends = (props) => {
    const [selectedMetric, setSelectedMetric] = useState('Orders');
    const [selectedGoal, setSelectedGoal] = useState('Daily');
    const [error, setError] = useState(undefined);
    const [loading, setLoading] = useState(true);

    const [dailyGoals, setDailyGoals] = useState(NEW_DEFAULT_GOALS());
    const [last7Metrics, setLast7Metrics] = useState(NEW_DEFAULT_METRICS());
    const [previous7Metrics, setPrevious7Metrics] = useState(NEW_DEFAULT_METRICS());
    const [lastMonthLast7Metrics, setLastMonthLast7Metrics] = useState(NEW_DEFAULT_METRICS());
    const [lastYearLast7Metrics, setLastYearLast7Metrics] = useState(NEW_DEFAULT_METRICS());
    const [last7Days, setLast7Days] = useState(undefined);
    // const [thisMonthLastYearData, setThisMonthLastYearData] = useState(undefined);
    // const [previousMonthLastYearData, setPreviousMonthLastYearData] = useState(undefined);

    const user = props.auth.user;
    const account = user?.activeAccount;
    const providers = getAccountProviders(account, [ProviderNames.ADAYA]); // get all account data except Adaya revenue
    const timezone = getAccountTimezone(account);

    const last7Filter = { timeRange: createIsoTimeRange('last7days') };
    const previous7Filter = { timeRange: createIsoTimeRange('previous7days') };
    const lastMonthLast7Filter = { timeRange: createIsoTimeRange('lastMonthLast7') };
    const lastYearLast7Filter = { timeRange: createIsoTimeRange('lastYearLast7') };

    // update the goals on user or account changes
    useEffect(() => {
        if (user && account) {
            const daysInMonth = today().daysInMonth();

            const revenueGoal = getGoal(account, Goal.Revenue);
            const cppGoal = getGoal(account, Goal.Cpp);
            const aovGoal = getGoal(account, Goal.Aov);
            const merGoal = getGoal(account, Goal.Mer);
            const roasGoal = getGoal(account, Goal.Roas);
            const adspendGoal = getGoal(account, Goal.Adspend);
            const ordersGoal = getGoal(account, Goal.Orders);

            setDailyGoals({
                orders: ordersGoal / daysInMonth,
                revenue: revenueGoal / daysInMonth,
                adspend: adspendGoal / daysInMonth,
                mer: merGoal,
                roas: roasGoal,
                cpp: cppGoal,
                aov: aovGoal,
            });
        }
    }, [user, account]);

    useEffect(async () => {
        await fetchAllData();
    }, [user]);

    const fetchAllData = async () => {
        setLoading(true);
        if (!account) return;
        await Promise.all([
            fetchData('last7', last7Filter),
            fetchData('previous7', previous7Filter),
            fetchData('lastMonthLast7', lastMonthLast7Filter),
            fetchData('lastYearLast7', lastYearLast7Filter)
        ])
        setLoading(false);
    }

    const fetchData = async (id, filter) => {
        if (!account) return;
        setLoading(true);

        try {
            filter.account = account;
            filter.providers = providers.map(p => p.name);
            const r = await pageHelper.getDailyCampaignDataRequest(filter);
            onDataResponse(r, filter, id);
        } catch (e) {
            setError(getInnerError(e));
        }

    }

    const onDataResponse = (r: ManagedRequest, filter, id) => {
        try {
            //log('onDataResponse', filter, id, r);
            if (r.status == RequestStatus.FINISHED) {
                const metrics = calculateMetricsAcrossDays(r.response);
                switch (id) {
                    case 'last7':
                        setLast7Metrics(metrics);
                        setLast7Days(Object.keys(r.response));
                        break;
                    case 'previous7':
                        setPrevious7Metrics(metrics);
                        break;
                    case 'lastMonthLast7':
                        setLastMonthLast7Metrics(metrics);
                        break;
                    case 'lastYearLast7':
                        setLastYearLast7Metrics(metrics);
                        break;
                    default:
                        break;
                }
                return;
            }

            setError(getInnerError(r));
        } catch (e) {
            log(`Exception`, e);
            throw e;
        }
    }

    const choosePrice = (selectedMetric) => {
        switch (selectedMetric) {
            case 'Orders':
                return 'even';
            case 'Revenue':
                return true;
            case 'MER':
                return false;
            case 'ROAS':
                return false;
            case 'Adspend':
                return true;
            case 'CPO':
                return true;
            case 'AOV':
                return true;
            default:
                return true;
        }
    }

    const chooseData = useCallback((selectedMetric, selectedGoal) => {
        let goal = dailyGoals.revenue;
        let last7Data = last7Metrics[`${getMetricProp(selectedMetric)}Data`];

        if (selectedGoal === 'Daily') {
            goal = dailyGoals[`${getMetricProp(selectedMetric)}`];
        } else if (selectedGoal === 'Previous7') {
            goal = previous7Metrics[`${getMetricProp(selectedMetric)}Avg`];
        } else if (selectedGoal === 'LastMonth') {
            goal = lastMonthLast7Metrics[`${getMetricProp(selectedMetric)}Avg`];
        } else {
            goal = lastYearLast7Metrics[`${getMetricProp(selectedMetric)}Avg`];
        }

        let rd = renderData(last7Data, goal, last7Days);
        return rd;
    }, [dailyGoals, previous7Metrics, lastMonthLast7Metrics, lastYearLast7Metrics, last7Metrics]);

    const chooseOptions = useCallback((selectedMetric, selectedGoal) => {
        let goal = '';
        let ds = last7Metrics, ds2 = undefined;
        let scale = { min: 0, max: 0 };
        let price = choosePrice(selectedMetric);

        if (selectedGoal === 'Daily') {
            scale = getMaxMin(last7Metrics[`${getMetricProp(selectedMetric)}Data`], dailyGoals[getMetricProp(selectedMetric)])
        } else if (selectedGoal === 'Previous7') {
            scale = getMaxMin(last7Metrics[`${getMetricProp(selectedMetric)}Data`], previous7Metrics[`${getMetricProp(selectedMetric)}Avg`])
        } else if (selectedGoal === 'LastMonth') {
            scale = getMaxMin(last7Metrics[`${getMetricProp(selectedMetric)}Data`], lastMonthLast7Metrics[`${getMetricProp(selectedMetric)}Avg`])
        } else {
            scale = getMaxMin(last7Metrics[`${getMetricProp(selectedMetric)}Data`], lastYearLast7Metrics[`${getMetricProp(selectedMetric)}Avg`])
        }

        let ro = renderOptions(scale.max, scale.min, price, last7Days);
        return ro;
    }, [dailyGoals, previous7Metrics, lastMonthLast7Metrics, lastYearLast7Metrics, last7Metrics]);

    const chooseActual = useCallback((selectedMetric) => {
        switch (selectedMetric) {
            case 'Orders':
                return last7Metrics.ordersAvg;
            case 'Revenue':
                return last7Metrics.revenueAvg;
            case 'Adspend':
                return last7Metrics.adspendAvg;
            case 'MER':
                return last7Metrics.merAvg;
            case 'ROAS':
                return last7Metrics.roasAvg;
            case 'CPO':
                return last7Metrics.cppAvg;
            case 'AOV':
                return last7Metrics.aovAvg;
            default:
                return dailyGoals.revenue;
        }
    }, [last7Metrics, dailyGoals]);

    const chooseTarget = useCallback((selectedMetric) => {
        let goal = dailyGoals.revenue;

        if (selectedGoal === 'Daily') {
            goal = dailyGoals[getMetricProp(selectedMetric)];
        } else if (selectedGoal === 'Previous7') {
            goal = previous7Metrics[`${getMetricProp(selectedMetric)}Avg`];
        } else if (selectedGoal === 'LastMonth') {
            goal = lastMonthLast7Metrics[`${getMetricProp(selectedMetric)}Avg`];
        } else if (selectedGoal === 'LastYear') {
            goal = lastYearLast7Metrics[`${getMetricProp(selectedMetric)}Avg`];
        }

        return orZero(goal);
    }, [dailyGoals, selectedGoal, previous7Metrics, lastMonthLast7Metrics, lastYearLast7Metrics, last7Metrics]);

    const chooseThreshold = (selectedMetric) => {
        switch (selectedMetric) {
            case 'CPO':
                return 124;
            default:
                return 74;
        }
    }

    const chooseComparison = (selectedGoal) => {
        switch (selectedGoal) {
            case 'Daily':
                return ''
            case 'Previous7':
                return 'the previous 7 days';
            case 'LastMonth':
                return 'this time previous month';
            case 'LastYear':
                return 'this time previous year';
            default:
                return '';
        }
    }

    const formatNum = (num, isPrice) => {
        switch (isPrice) {
            case 'even':
                return `${Math.floor(num)}`;
            case false:
                return `${parseFloat(num).toFixed(2)}`;
            default:
                return `${formatCurrency(num)}`;
        }
    }

    const formatGoal = (selectedGoal) => {
        switch (selectedGoal.toLowerCase()) {
            case 'daily':
                return 'Daily Goal';
            case 'previous7':
                return 'Previous Week';
            case 'lastmonth':
                return 'This time previous month';
            case 'lastyear':
                return 'This time previous year';
            default:
                return 'Previous AVG';
        }
    }

    const getDeltaPercentage = (avg, goal) => {
        if (avg == 0 || goal == 0)  return 0;

        let ratio;
        if (selectedMetric.toLowerCase() !== 'cpo') {
            ratio = (avg - goal) / goal;
        } else {
            ratio = (goal - avg) / avg;
        }

        return parseFloat(ratio * 100).toFixed(2);
    }

    const updateGoal = (g) => {
        setSelectedGoal(g);
    }

    const GraphBlock = (appendedClass) => {
        return (
            <div className={`trends-graph-container ${appendedClass}`}>

                <div className="trends-select-container">
                    <Dropdown
                        selectedItem={metricItems.find(m => m.value == selectedMetric).label}
                        setSelection={setSelectedMetric}
                        items={metricItems || []}
                    />
                    <Dropdown
                        selectedItem={goalItems.find(m => m.value == selectedGoal).label}
                        setSelection={updateGoal}
                        items={goalItems || []}
                    />
                </div>

                <LineGraph
                    data={chooseData(selectedMetric, selectedGoal)}
                    options={chooseOptions(selectedMetric, selectedGoal)}
                    gradientColors={gradientColors}
                />

            </div>
        )
    }

    const hasData = () => {
        // true
        return true;
    }

    const dailyGoal = chooseTarget(selectedMetric);
    const average = chooseActual(selectedMetric);
    const deltaPercentage = getDeltaPercentage(average, dailyGoal);
    const isPrice = choosePrice(selectedMetric);

    return (
        <div className={'trends-page ' + (error != undefined ? ' has-error' : '')}>
            <Helmet><title>{Config.SITE_TITLE_PREFIX} - Trends</title></Helmet>

            <div className="page-layout">
                {loading && renderLoader()}

                {!loading && (

                    // error
                    error ? renderError(ErrorMessages.defaultErrorMessage(), error, "large light")

                        // loaded but has no data
                        : (
                            !hasData() ? renderNoDataMessage()

                                // loaded and has data
                                : (
                                    <>
                                        <div className="col">

                                            <div className="header-block">
                                                <div className="header">Trends</div>
                                                <GeneralMessage
                                                    title={getMetricLongName(selectedMetric)}
                                                    comparison={chooseComparison(selectedGoal)}
                                                    percentageDaily={deltaPercentage}
                                                />
                                            </div>

                                            {GraphBlock('mobile')}

                                            <div className="details-block">
                                                <div className="inner">

                                                    <div className="details">
                                                        <h6 className="green">Avg Daily {getMetricLongName(selectedMetric)}</h6>
                                                        <div>
                                                            <h1 className="number green">{formatNum(average, isPrice)}</h1>
                                                            <span className={`percentage ${deltaPercentage > 0 ? 'green' : 'orange'}`}>{`${deltaPercentage}%`}</span>
                                                        </div>
                                                    </div>

                                                    <div className="details">
                                                        <h6 className="purple">{formatGoal(selectedGoal)}</h6>
                                                        <div>
                                                            <h1 className="number purple">{formatNum(dailyGoal, isPrice)}</h1>
                                                        </div>
                                                    </div>

                                                </div>
                                            </div>
                                        </div>

                                        <div className="section">
                                            {GraphBlock('desktop')}
                                        </div>
                                    </>
                                )
                        )

                )}
            </div>
        </div>
    )
}